/**
 * The org doc's staff-set flags, and the demo-org callables.
 */
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../config'

/** Flags on `organizations/{orgId}`; each absent flag reads as off. */
export interface OrgFlags {
  isDemo: boolean
  smsRecoveryEnabled: boolean
  benefitsCaptureEnabled: boolean
  /** As stored; resolve with `resolveFrontDeskGoal` (shared/frontDeskGoal). */
  frontDeskGoal: unknown
}

function readFlags(data: Record<string, unknown> | undefined): OrgFlags {
  return {
    isDemo: data?.isDemo === true,
    smsRecoveryEnabled: data?.smsRecoveryEnabled === true,
    benefitsCaptureEnabled: data?.benefitsCaptureEnabled === true,
    frontDeskGoal: data?.frontDeskGoal,
  }
}

/** Live org flags, so a staff toggle shows without a reload. Returns the unsubscribe. */
export function subscribeOrgFlags(
  orgId: string,
  onData: (flags: OrgFlags) => void,
  onError: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'organizations', orgId),
    snap => onData(readFlags(snap.data())),
    onError,
  )
}

/** One-time read of whether the org is a demo org. */
export async function isDemoOrg(orgId: string): Promise<boolean> {
  const snap = await getDoc(doc(db, 'organizations', orgId))
  return readFlags(snap.data()).isDemo
}

/** Rebuilds a demo org's today and tomorrow; returns the number of patients seeded. */
export async function resetDemoOrg(orgId: string): Promise<{ seeded: number }> {
  const fn = httpsCallable<{ orgId: string }, { seeded: number }>(functions, 'resetDemoOrg')
  const res = await fn({ orgId })
  return res.data
}

/** Sends a demo org's unchecked patients for tomorrow through the pipeline. */
export async function runDemoBatch(orgId: string): Promise<{ processed: number; message?: string }> {
  const fn = httpsCallable<{ orgId: string }, { processed: number; message?: string }>(functions, 'runDemoBatch')
  const res = await fn({ orgId })
  return res.data
}
